import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import BottomNav from '../components/BottomNav';

const PlusIcon = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>);
const BackIcon = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>);
const TrashIcon = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>);
const EditIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>);
const UploadIcon = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>);
const ChevronRight = () => (<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="9 18 15 12 9 6"/></svg>);

const USER_CATS = [
  { id: 'hands', label: 'Hands', emoji: '🥁', color: '#FF5500' },
  { id: '4way', label: '4-Way', emoji: '🎯', color: '#00B4D8' },
  { id: 'grooves', label: 'Grooves', emoji: '🎵', color: '#7B2FBE' },
  { id: 'book', label: 'Book', emoji: '📖', color: '#F9A825' },
  { id: 'sheets', label: 'Sheets', emoji: '📄', color: '#22C55E' },
  { id: 'others', label: 'Others', emoji: '⭐', color: '#EC4899' },
];

const inputStyle = { width:'100%', background:'#1C1916', border:'1.5px solid #2E2924', borderRadius:8, color:'#F0EBE3', fontFamily:"'Barlow', sans-serif", fontSize:14, padding:'10px 12px', outline:'none' };
const labelStyle = { fontSize:11, color:'#7A7268', fontFamily:"'Barlow Condensed', sans-serif", fontWeight:700, letterSpacing:1.5, textTransform:'uppercase', display:'block', marginBottom:6 };

export default function MyExercisesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exercises, setExercises] = useState([]);
  const [userSubs, setUserSubs] = useState([]); // user's own subcategories
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('grid'); // grid | category
  const [activeCategory, setActiveCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showSubForm, setShowSubForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editId, setEditId] = useState(null);
  const [newSubName, setNewSubName] = useState('');
  const fileRef = useRef();

  const [form, setForm] = useState({
    title: '', description: '', image_url: '',
    default_bpm: 80, default_time_sig: '4/4', user_category: 'hands', user_subcategory_id: '',
  });

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    const [{ data: exs }, { data: subs }] = await Promise.all([
      supabase.from('user_exercises').select('*').eq('user_id', user.id).order('created_at', { ascending: false }),
      supabase.from('user_subcategories').select('*').eq('user_id', user.id).order('name'),
    ]);
    if (exs) setExercises(exs);
    if (subs) setUserSubs(subs);
    setLoading(false);
  };

  const openNew = (catId) => {
    setEditId(null);
    setForm({ title:'', description:'', image_url:'', default_bpm:80, user_category: catId || activeCategory?.id || 'hands', user_subcategory_id:'' });
    setShowForm(true);
  };

  const openEdit = (ex) => {
    setEditId(ex.id);
    setForm({ title:ex.title, description:ex.description||'', image_url:ex.image_url||'', default_bpm:ex.default_bpm||80, default_time_sig:ex.default_time_sig||'4/4', user_category:ex.user_category||'hands', user_subcategory_id:ex.user_subcategory_id||'' });
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const path = `user-${user.id}-${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('exercises').upload(path, file, { upsert: true });
      if (error) throw error;
      const { data: { publicUrl } } = supabase.storage.from('exercises').getPublicUrl(path);
      setForm(f => ({ ...f, image_url: publicUrl }));
    } catch (err) { alert('Upload error: ' + err.message); }
    setUploading(false);
  };

  const save = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    const data = {
      user_id: user.id, title: form.title, description: form.description,
      image_url: form.image_url || null, default_bpm: parseInt(form.default_bpm) || 80,
      user_category: form.user_category,
      user_subcategory_id: parseInt(form.user_subcategory_id) || null,
      default_time_sig: form.default_time_sig || '4/4',
    };
    if (editId) await supabase.from('user_exercises').update(data).eq('id', editId);
    else await supabase.from('user_exercises').insert(data);
    setSaving(false); setShowForm(false); loadAll();
  };

  const createSub = async () => {
    if (!newSubName.trim()) return;
    const { data } = await supabase.from('user_subcategories').insert({
      user_id: user.id, name: newSubName, user_category: form.user_category,
    }).select().single();
    if (data) {
      setUserSubs(prev => [...prev, data]);
      setForm(f => ({ ...f, user_subcategory_id: String(data.id) }));
    }
    setNewSubName(''); setShowSubForm(false);
  };

  const deleteEx = async () => {
    if (!confirmDelete) return;
    await supabase.from('user_exercises').delete().eq('id', confirmDelete.id);
    setConfirmDelete(null); loadAll();
  };

  const catExercises = (catId) => exercises.filter(e => e.user_category === catId);
  const catSubs = (catId) => userSubs.filter(s => s.user_category === catId);
  const formSubs = userSubs.filter(s => s.user_category === form.user_category);
  const activeCat = USER_CATS.find(c => c.id === activeCategory?.id);

  return (
    <div style={{ fontFamily:"'Barlow', sans-serif", background:'#111009', minHeight:'100vh', color:'#F0EBE3', maxWidth:'var(--app-width, 860px)', margin:'0 auto', paddingBottom:80 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@300;400;500;600;700&family=Barlow+Condensed:wght@400;600;700;800;900&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        button { cursor: pointer; border: none; font-family: inherit; transition: all 0.15s; }
        button:active { transform: scale(0.96); opacity: 0.82; }
        @keyframes fadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:none} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .fu { animation: fadeUp 0.25s ease forwards; }
        .bg-grid { position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background-image: linear-gradient(rgba(255,85,0,0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,85,0,0.02) 1px, transparent 1px);
          background-size: 40px 40px; }
        .cat-card { background:#1C1916; border:1px solid #2A2520; border-radius:12px; cursor:pointer; transition:all 0.18s; overflow:hidden; position:relative; }
        .cat-card:active { transform: scale(0.97); }
        .ex-card { background:#1C1916; border:1px solid #2A2520; border-radius:12px; overflow:hidden; cursor:pointer; transition:all 0.18s; }
        .ex-card:active { transform: scale(0.97); opacity: 0.85; }
      `}</style>
      <div className="bg-grid"/>

      {/* Confirm Delete */}
      {confirmDelete && (
        <div style={{ position:'fixed', inset:0, zIndex:400, background:'rgba(0,0,0,0.9)', display:'flex', alignItems:'center', justifyContent:'center', backdropFilter:'blur(6px)', padding:'0 20px' }}>
          <div style={{ background:'#171410', border:'1px solid #3A2020', borderRadius:16, padding:'28px 24px', width:'100%', maxWidth:340, textAlign:'center' }}>
            <div style={{ fontSize:44, marginBottom:12 }}>⚠️</div>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:20, fontWeight:800, color:'#F0EBE3', marginBottom:12 }}>ARE YOU SURE?</div>
            <div style={{ fontSize:14, color:'#FF5500', fontWeight:700, marginBottom:20, padding:'8px 12px', background:'rgba(255,85,0,0.1)', borderRadius:8 }}>"{confirmDelete.title}"</div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setConfirmDelete(null)} style={{ flex:1, padding:'13px', background:'#2A2520', color:'#C4B9AC', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:14 }}>CANCEL</button>
              <button onClick={deleteEx} style={{ flex:1, padding:'13px', background:'#CC2200', color:'#fff', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:14 }}>DELETE</button>
            </div>
          </div>
        </div>
      )}

      {/* Exercise Form Modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, zIndex:200, background:'rgba(0,0,0,0.87)', display:'flex', alignItems:'flex-end', backdropFilter:'blur(4px)' }}>
          <div style={{ background:'#171410', borderRadius:'20px 20px 0 0', padding:'28px 20px 44px', width:'100%', maxHeight:'92vh', overflowY:'auto' }}>
            <div style={{ fontFamily:"'Barlow Condensed'", fontSize:20, fontWeight:800, letterSpacing:2, marginBottom:20, color:'#F0EBE3' }}>
              {editId ? 'EDIT' : 'NEW EXERCISE'}
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Title *</label>
              <input style={inputStyle} value={form.title} onChange={e => setForm(f=>({...f,title:e.target.value}))} placeholder="Ex: Modern Drummer p.45, Paradiddle..."/>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Description / Notes</label>
              <textarea style={{ ...inputStyle, resize:'vertical' }} rows={3} value={form.description} onChange={e => setForm(f=>({...f,description:e.target.value}))} placeholder="Book, page, notes..."/>
            </div>

            {/* Category selector */}
            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Category</label>
              <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                {USER_CATS.map(cat => (
                  <button key={cat.id} onClick={() => setForm(f=>({...f, user_category:cat.id, user_subcategory_id:''}))}
                    style={{ padding:'6px 10px', borderRadius:8, background: form.user_category===cat.id?(cat.color+'22'):'#1C1916', border:`1.5px solid ${form.user_category===cat.id?cat.color:'#2A2520'}`, color: form.user_category===cat.id?cat.color:'#7A7268', fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:12, letterSpacing:1 }}>
                    {cat.emoji} {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Subcategory selector + create */}
            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Subcategory</label>
              {formSubs.length > 0 && (
                <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:8 }}>
                  <button onClick={() => setForm(f=>({...f, user_subcategory_id:''}))}
                    style={{ padding:'5px 10px', borderRadius:8, background: !form.user_subcategory_id?'#FF5500':'#1C1916', color: !form.user_subcategory_id?'#fff':'#7A7268', border:`1px solid ${!form.user_subcategory_id?'#FF5500':'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11 }}>
                    Nenhuma
                  </button>
                  {formSubs.map(sub => (
                    <button key={sub.id} onClick={() => setForm(f=>({...f, user_subcategory_id:String(sub.id)}))}
                      style={{ padding:'5px 10px', borderRadius:8, background: form.user_subcategory_id===String(sub.id)?'#FF5500':'#1C1916', color: form.user_subcategory_id===String(sub.id)?'#fff':'#7A7268', border:`1px solid ${form.user_subcategory_id===String(sub.id)?'#FF5500':'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11 }}>
                      {sub.name}
                    </button>
                  ))}
                </div>
              )}
              {!showSubForm ? (
                <button onClick={() => setShowSubForm(true)} style={{ background:'transparent', color:'#FF5500', fontSize:12, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1, display:'flex', alignItems:'center', gap:4 }}>
                  <PlusIcon/> CREATE SUBCATEGORY
                </button>
              ) : (
                <div style={{ display:'flex', gap:8 }}>
                  <input style={{ ...inputStyle, flex:1 }} value={newSubName} onChange={e => setNewSubName(e.target.value)} placeholder="Nome da subcategoria" autoFocus/>
                  <button onClick={createSub} style={{ background:'#FF5500', color:'#fff', padding:'0 14px', borderRadius:8, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:13, whiteSpace:'nowrap' }}>OK</button>
                  <button onClick={() => { setShowSubForm(false); setNewSubName(''); }} style={{ background:'#2A2520', color:'#9A9088', padding:'0 10px', borderRadius:8 }}>✕</button>
                </div>
              )}
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Suggested BPM</label>
              <input type="number" style={inputStyle} min={20} max={300} value={form.default_bpm} onChange={e => setForm(f=>({...f,default_bpm:e.target.value}))}/>
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={labelStyle}>Time Signature</label>
              <div style={{ display:'flex', gap:6 }}>
                {['2/4','3/4','4/4','5/4','6/8','7/8'].map(ts => (
                  <button key={ts} onClick={() => setForm(f=>({...f,default_time_sig:ts}))}
                    style={{ flex:1, padding:'8px 4px', borderRadius:8, background: form.default_time_sig===ts?'#FF5500':'#1C1916', color: form.default_time_sig===ts?'#fff':'#7A7268', border:`1px solid ${form.default_time_sig===ts?'#FF5500':'#2A2520'}`, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:13, cursor:'pointer' }}>
                    {ts}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={labelStyle}>Image (optional)</label>
              <div style={{ display:'flex', gap:10, alignItems:'center' }}>
                {form.image_url
                  ? <img src={form.image_url} alt="" style={{ width:52, height:52, objectFit:'cover', objectPosition:'50% 24%', borderRadius:8, border:'1px solid #2E2924', flexShrink:0 }}/>
                  : <div style={{ width:52, height:52, background:'#2A2520', borderRadius:8, border:'1.5px dashed #3A3530', display:'flex', alignItems:'center', justifyContent:'center', color:'#5A5248', flexShrink:0 }}><UploadIcon/></div>
                }
                <div>
                  <button onClick={() => fileRef.current.click()} style={{ background:'#2A2520', color:'#C4B9AC', padding:'7px 12px', borderRadius:8, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:12, letterSpacing:1, display:'flex', alignItems:'center', gap:5 }}>
                    <UploadIcon/> {uploading ? 'LOADING…' : form.image_url ? 'CHANGE' : 'UPLOAD'}
                  </button>
                  <div style={{ fontSize:11, color:'#4A4038', marginTop:4 }}>Photo, sheet, PDF screenshot</div>
                </div>
                <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display:'none' }}/>
              </div>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setShowForm(false)} style={{ flex:1, padding:'13px', background:'#2A2520', color:'#9A9088', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:14, letterSpacing:1 }}>CANCEL</button>
              <button onClick={save} disabled={!form.title.trim() || saving} style={{ flex:2, padding:'13px', background:'#FF5500', color:'#fff', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:14, letterSpacing:2, display:'flex', alignItems:'center', justifyContent:'center' }}>
                {saving ? <div style={{ width:18, height:18, borderRadius:'50%', border:'2.5px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'spin 0.7s linear infinite' }}/> : (editId ? 'SAVE' : 'CREATE')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── GRID VIEW — Categories ── */}
      {view === 'grid' && (
        <>
          <div style={{ padding:'52px 20px 20px', background:'linear-gradient(180deg, #FF550010 0%, #111009 100%)', position:'relative', zIndex:1 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:32, fontWeight:900, letterSpacing:1, color:'#F0EBE3', lineHeight:1 }}>MY STUDIES</h1>
                <div style={{ fontSize:12, color:'#7A7268', marginTop:4 }}>{exercises.length} personal exercises</div>
              </div>
              <button onClick={() => openNew(null)} style={{ background:'#FF5500', color:'#fff', width:44, height:44, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center' }}>
                <PlusIcon/>
              </button>
            </div>
          </div>

          <div style={{ padding:'16px', position:'relative', zIndex:1 }}>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(var(--cat-cols, 3), 1fr)', gap:10 }}>
              {USER_CATS.map((cat, i) => {
                const count = catExercises(cat.id).length;
                return (
                  <div key={cat.id} className={`cat-card fu`} style={{ animationDelay:`${i*0.04}s`, opacity:0 }}
                    onClick={() => { setActiveCategory(cat); setView('category'); }}>
                    <div style={{ height:80, background:cat.color+'18', display:'flex', alignItems:'center', justifyContent:'center', fontSize:34, position:'relative' }}>
                      {cat.emoji}
                      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:2, background:cat.color }}/>
                    </div>
                    <div style={{ padding:'8px 10px', textAlign:'center' }}>
                      <div style={{ fontSize:12, fontWeight:700, color:'#F0EBE3' }}>{cat.label}</div>
                      <div style={{ fontSize:10, color:'#5A5248', marginTop:2 }}>{count} ex.</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* ── CATEGORY VIEW — Exercises ── */}
      {view === 'category' && activeCat && (
        <>
          <div style={{ padding:'52px 20px 20px', background:`linear-gradient(180deg, ${activeCat.color}15 0%, #111009 100%)`, position:'relative', zIndex:1 }}>
            <button onClick={() => setView('grid')} style={{ background:'transparent', color:'#7A7268', marginBottom:16, display:'flex', alignItems:'center', gap:6, fontSize:13, fontFamily:"'Barlow Condensed'", fontWeight:700, letterSpacing:1 }}>
              <BackIcon/> MY STUDIES
            </button>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:14 }}>
                <div style={{ width:52, height:52, borderRadius:12, background:activeCat.color+'22', border:`2px solid ${activeCat.color}44`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:26 }}>{activeCat.emoji}</div>
                <div>
                  <h1 style={{ fontFamily:"'Barlow Condensed'", fontSize:28, fontWeight:900, letterSpacing:1, color:'#F0EBE3', lineHeight:1 }}>{activeCat.label}</h1>
                  <div style={{ fontSize:12, color:'#7A7268', marginTop:3 }}>{catExercises(activeCat.id).length} exercises</div>
                </div>
              </div>
              <button onClick={() => openNew(activeCat.id)} style={{ background:activeCat.color, color:'#fff', width:40, height:40, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <PlusIcon/>
              </button>
            </div>

            {/* Subcategory filter */}
            {catSubs(activeCat.id).length > 0 && (
              <div style={{ display:'flex', gap:6, marginTop:16, overflowX:'auto', paddingBottom:4, marginLeft:-20, paddingLeft:20, marginRight:-20, paddingRight:20 }}>
                {catSubs(activeCat.id).map(sub => (
                  <span key={sub.id} style={{ padding:'4px 10px', borderRadius:20, background:activeCat.color+'22', border:`1px solid ${activeCat.color}44`, color:activeCat.color, fontFamily:"'Barlow Condensed'", fontWeight:700, fontSize:11, letterSpacing:1, flexShrink:0 }}>
                    {sub.name}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{ padding:'16px', position:'relative', zIndex:1 }}>
            {catExercises(activeCat.id).length === 0 ? (
              <div style={{ textAlign:'center', padding:'50px 20px', color:'#3A3530' }}>
                <div style={{ fontSize:40, marginBottom:10 }}>{activeCat.emoji}</div>
                <div style={{ fontFamily:"'Barlow Condensed'", fontSize:15, fontWeight:700, letterSpacing:1, marginBottom:8 }}>NO EXERCISES</div>
                <button onClick={() => openNew(activeCat.id)} style={{ background:activeCat.color, color:'#fff', padding:'10px 24px', borderRadius:10, fontFamily:"'Barlow Condensed'", fontWeight:800, fontSize:13, letterSpacing:1, marginTop:8 }}>+ CREATE</button>
              </div>
            ) : (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(var(--content-cols, 2), 1fr)', gap:10 }}>
                {catExercises(activeCat.id).map((ex, i) => {
                  const sub = userSubs.find(s => s.id === ex.user_subcategory_id);
                  return (
                    <div key={ex.id} className={`ex-card fu`} style={{ animationDelay:`${i*0.04}s`, opacity:0 }}>
                      <div onClick={() => navigate(`/exercise/user-${ex.id}`, { state: { userExercise: true, defaultTimeSig: ex.default_time_sig || '4/4' } })}
                        style={{ height:100, overflow:'hidden', position:'relative', background:activeCat.color+'18' }}>
                        {ex.image_url
                          ? <img src={ex.image_url} alt="" style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'50% 24%' }}/>
                          : <div style={{ width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:32 }}>{activeCat.emoji}</div>
                        }
                        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, #1C1916 0%, transparent 60%)' }}/>
                        {sub && (
                          <div style={{ position:'absolute', top:6, left:6, background:activeCat.color+'22', border:`1px solid ${activeCat.color}44`, borderRadius:6, padding:'2px 6px' }}>
                            <span style={{ fontSize:9, fontWeight:700, color:activeCat.color, fontFamily:"'Barlow Condensed'", letterSpacing:1 }}>{sub.name}</span>
                          </div>
                        )}
                      </div>
                      <div style={{ padding:'10px 12px 12px' }}>
                        <div onClick={() => navigate(`/exercise/user-${ex.id}`, { state: { userExercise: true, defaultTimeSig: ex.default_time_sig || '4/4' } })} style={{ fontSize:13, fontWeight:700, color:'#F0EBE3', marginBottom:4, lineHeight:1.3 }}>{ex.title}</div>
                        <div style={{ fontSize:11, color:'#FF5500', fontFamily:"'Barlow Condensed'", fontWeight:700, marginBottom:8 }}>♩ {ex.default_bpm} BPM</div>
                        <div style={{ display:'flex', gap:5 }}>
                          <button onClick={() => navigate('/programs', { state: { exercise: { id: `user-${ex.id}`, title: ex.title, image_url: ex.image_url, default_bpm: ex.default_bpm, isUserExercise: true } } })} style={{ flex:1, background:'#2A2520', color:'#9A9088', padding:'6px 0', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontFamily:"'Barlow Condensed'", fontWeight:700 }}>+</button>
                          <button onClick={() => openEdit(ex)} style={{ flex:1, background:'#2A2520', color:'#9A9088', padding:'6px 0', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}><EditIcon/></button>
                          <button onClick={() => setConfirmDelete(ex)} style={{ flex:1, background:'#2A1010', color:'#FF6666', padding:'6px 0', borderRadius:7, display:'flex', alignItems:'center', justifyContent:'center' }}><TrashIcon/></button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      <BottomNav/>
    </div>
  );
}
